// SalesCalls.jsx — shorter + fixed (tabs nesting, extra braces, react-bootstrap Badge props, fewer imports)

import React, { useMemo, useState } from "react";
import { Container, Table, Button, Form, Modal, Row, Col, Badge, Tabs, Tab, Alert, Card } from "react-bootstrap";
import { FaPlus, FaPhone, FaEdit, FaSpinner, FaTrash, FaDownload } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import CSVUploader from "../components/CSVUploader";
import LoadingSpinner from "../components/LoadingSpinner";
import { salesCallsAPI } from "../services/api";
import "../styles/LeadManagement.css";

const PAGE_SIZE = 50;

const StatusBadge = ({ status }) => {
  const cfg =
    {
      not_called: { bg: "secondary", label: "not_called" },
      started: { bg: "primary", label: "started" },
      ongoing: { bg: "info", label: "ongoing" },
      completed: { bg: "success", label: "completed" },
      no_answer: { bg: "warning", label: "no_answer" },
      voicemail: { bg: "secondary", label: "voicemail" },
      failed: { bg: "danger", label: "failed" },
      invalid_number: { bg: "dark", label: "invalid_number" },
      
    }[status] || { bg: "secondary", label: status || "unknown" };

  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
};

const InterestBadge = ({ interest }) => {
  const cfg =
    {
      yes: { bg: "success", label: "yes" },
      no: { bg: "danger", label: "no" },
      unclear: { bg: "warning", label: "unclear" },
    }[interest] || { bg: "secondary", label: interest || "unknown" };

  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
};

const emptyLead = { name: "", service: "", phone_number: "", address: "", city: "", state: "", year: "" };
const formatLocation = (item) => {
  const parts = [
    item?.address?.trim(),
    item?.city?.trim(),
    item?.state?.trim(),
  ].filter(Boolean);

  return parts.length ? parts.join(", ") : "-";
};

const BatchCallModal = ({ show, onHide, batchLeadsQ, batchFilters, setBatchFilters, selectedLeads, setSelectedLeads, batchCallName, setBatchCallName, batchCallM, batchCallFilteredM, handleSelectLead, handleSelectAll, batchLeadsPage, setBatchLeadsPage, pager }) => {
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Call Leads</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Call Name</Form.Label>
              <Form.Control
                value={batchCallName}
                onChange={(e) => setBatchCallName(e.target.value)}
                placeholder="Enter Campaign name (optional)"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Filter by Call Status</Form.Label>
              <Form.Select value={batchFilters.call_status} onChange={(e) => setBatchFilters((p) => ({ ...p, call_status: e.target.value }))}>
                <option value="">All Leads</option>
                <option value="not_called">Not Called</option>
                <option value="no_answer">No Answer</option>
                <option value="voicemail">Voicemail</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {batchLeadsQ.isLoading ? (
          <LoadingSpinner />
        ) : batchLeadsQ.error ? (
          <Alert variant="danger">Error loading leads: {batchLeadsQ.error.message}</Alert>
        ) : (
          <>
            <div className="mb-3">
              <Button variant="outline-secondary" size="sm" onClick={handleSelectAll}>
                {selectedLeads.length === batchLeadsQ.data?.results?.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="ms-2">Selected: {selectedLeads.length}</span>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {batchLeadsQ.data?.results?.length ? (
                batchLeadsQ.data.results.map((lead) => (
                  <Card key={lead.id} className="mb-3">
                    <Card.Body>
                      <Card.Header className="d-flex align-items-center">
                        <Form.Check
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="me-2"
                        />
                        <Card.Title className="mb-0">{lead.name}</Card.Title>
                      </Card.Header>
                      <Card.Text><strong>Phone:</strong> {lead.phone_number}</Card.Text>
                      <Card.Text><strong>Service:</strong> {lead.service || "-"}</Card.Text>
                      <Card.Text><strong>Location:</strong> {lead.address && lead.city && lead.state ? `${lead.city}, ${lead.state}` : lead.address || "-"}</Card.Text>
                      <Card.Text><strong>Call Status:</strong> <StatusBadge status={lead.call_status} /></Card.Text>
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <Alert variant="info" className="text-center">No leads found</Alert>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>
                        <Form.Check
                          checked={selectedLeads.length === batchLeadsQ.data?.results?.length && batchLeadsQ.data?.results?.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Service</th>
                      <th>Location</th>
                      <th>Call Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchLeadsQ.data?.results?.length ? (
                      batchLeadsQ.data.results.map((lead) => (
                        <tr key={lead.id}>
                          <td>
                            <Form.Check
                              checked={selectedLeads.includes(lead.id)}
                              onChange={() => handleSelectLead(lead.id)}
                            />
                          </td>
                          <td>{lead.name}</td>
                          <td>{lead.phone_number}</td>
                          <td>{lead.service || "-"}</td>
                          <td>{lead.city && lead.state ? `${lead.address},${lead.city}, ${lead.state}` : lead.address || "-"}</td>
                          <td><StatusBadge status={lead.call_status} /></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No leads found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            {pager(batchLeadsQ.data, batchLeadsPage, setBatchLeadsPage)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-column flex-sm-row">
        <Button variant="secondary" onClick={onHide} className="mb-2 mb-sm-0">
          Cancel
        </Button>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <Button
            variant="warning"
            onClick={() => batchCallFilteredM.mutate({ filters: batchFilters, callName: batchCallName || `Campaign${Date.now()}` })}
            disabled={batchCallFilteredM.isPending}
          >
            {batchCallFilteredM.isPending ? <FaSpinner className="fa-spin me-2" /> : <FaPhone className="me-2" />}
            Call All Filtered
          </Button>
          <Button
            variant="success"
            onClick={() => batchCallM.mutate({ lead_ids: selectedLeads, call_name: batchCallName || `Campaign${Date.now()}` })}
            disabled={batchCallM.isPending || selectedLeads.length === 0}
          >
            {batchCallM.isPending ? <FaSpinner className="fa-spin me-2" /> : <FaPhone className="me-2" />}
            Start Batch Call (Selected)
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const CallDetailsModal = ({ show, onHide, call }) => {
  if (!call) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Call Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12} md={6}>
            <h5>Basic Information</h5>
            <p><strong>Customer:</strong> {call.customer_name || "-"}</p>
            <p><strong>Phone:</strong> {call.phone_number || "-"}</p>
            <p><strong>Status:</strong> <StatusBadge status={call.status} /></p>
            <p><strong>Created:</strong> {call.created_at ? new Date(call.created_at).toLocaleString() : "-"}</p>
          </Col>
          <Col xs={12} md={6}>
            <h5>Call Outcomes</h5>
            <p><strong>Interest:</strong> {call.interest ? <InterestBadge interest={call.interest} /> : "-"}</p>
            <p><strong>Address:</strong> {call.address }</p>
            <p><strong>Callback Requested:</strong> {call.callback_requested ? <Badge bg={call.callback_requested === "yes" ? "warning" : "secondary"}>{call.callback_requested}</Badge> : "-"}</p>
            <p><strong>Voicemail:</strong> {call.voicemail ? <Badge bg={call.voicemail === "yes" ? "warning" : "secondary"}>{call.voicemail}</Badge> : "-"}</p>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <h5>Notes</h5>
            <p>{call.notes || "No notes available"}</p>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <h5>Transcript</h5>
            <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ccc", padding: "10px" }}>
              {call.transcript ? (
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{call.transcript}</pre>
              ) : (
                "No transcript available"
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function SalesCalls() {
  const qc = useQueryClient();

  // UI state
  const [activeTab, setActiveTab] = useState("leads");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // applied search (triggers query)
  
  // filters
  const [leadFilters, setLeadFilters] = useState({ call_status: "", qualified: "", service: "", state: "" });
  const [callFilters, setCallFilters] = useState({ status: "", interest: "", voicemail: "" });
  const [batchFilters, setBatchFilters] = useState({ call_status: "not_called", qualified: "", service: "", state: "" });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [batchCallName, setBatchCallName] = useState("");

  // paging
  const [leadsPage, setLeadsPage] = useState(1);
  const [callsPage, setCallsPage] = useState(1);
  const [batchLeadsPage, setBatchLeadsPage] = useState(1);

  // modals/forms
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [showCallDetails, setShowCallDetails] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [createForm, setCreateForm] = useState(emptyLead);
  const [editForm, setEditForm] = useState(emptyLead);

  // NOTE: You had a batch-call modal state but no way to open it + lots of extra UI.
  // Keeping this file short: batch calling removed. Re-add later if needed.

  // Reset page when filters/search/tab changes to avoid “empty page” confusion
  React.useEffect(() => setLeadsPage(1), [search, leadFilters]);
  React.useEffect(() => setCallsPage(1), [search, callFilters]);  React.useEffect(() => {
    if (showBatch && !batchCallName) {
      setBatchCallName(`Campaign${Date.now()}`);
    }
  }, [showBatch, batchCallName]);
  const leadsKey = useMemo(() => ["salesLeads", search, leadFilters, leadsPage], [search, leadFilters, leadsPage]);
  const callsKey = useMemo(() => ["salesCalls", search, callFilters, callsPage], [search, callFilters, callsPage]);
  const batchLeadsKey = useMemo(() => ["batchLeads", batchFilters, batchLeadsPage], [batchFilters, batchLeadsPage]);

  const leadsQ = useQuery({
    queryKey: leadsKey,
    queryFn: () =>
      salesCallsAPI.getLeads({
        q: search,
        ...leadFilters,
        page: leadsPage,
        page_size: PAGE_SIZE,
      }),
    enabled: activeTab === "leads",
    keepPreviousData: true,
  });

  const callsQ = useQuery({
    queryKey: callsKey,
    queryFn: () =>
      salesCallsAPI.getCalls({
        q: search,
        ...callFilters,
        page: callsPage,
        page_size: PAGE_SIZE,
      }),
    enabled: activeTab === "calls",
    keepPreviousData: true,
  });

  const batchLeadsQ = useQuery({
    queryKey: batchLeadsKey,
    queryFn: () =>
      salesCallsAPI.getLeads({
        ...batchFilters,
        page: batchLeadsPage,
        page_size: PAGE_SIZE,
      }),
    enabled: showBatch,
    keepPreviousData: true,
  });

  const createLeadM = useMutation({
    mutationFn: salesCallsAPI.createLead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      setShowCreate(false);
      setCreateForm(emptyLead);
      toast.success("Lead created");
    },
    onError: (e) => toast.error(`Create failed: ${e?.message || "Unknown error"}`),
  });

  const updateLeadM = useMutation({
    mutationFn: ({ id, data }) => salesCallsAPI.updateLead(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      setShowEdit(false);
      setEditingLead(null);
      toast.success("Lead updated");
    },
    onError: (e) => toast.error(`Update failed: ${e?.message || "Unknown error"}`),
  });

  const deleteLeadM = useMutation({
    mutationFn: (leadId) => salesCallsAPI.deleteLead(leadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      toast.success("Lead deleted");
    },
    onError: (e) => toast.error(`Delete failed: ${e?.message || "Unknown error"}`),
  });

  const singleCallM = useMutation({
    mutationFn: salesCallsAPI.makeSingleCall,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      qc.invalidateQueries({ queryKey: ["salesCalls"] });
      toast.success("Call initiated");
    },
    onError: (e) => toast.error(`Call failed: ${e?.message || "Unknown error"}`),
  });

  const importLeadsM = useMutation({
    mutationFn: salesCallsAPI.importLeads,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      toast.success("Leads imported");
    },
    onError: (e) => toast.error(`Import failed: ${e?.message || "Unknown error"}`),
  });

  const batchCallM = useMutation({
    mutationFn: salesCallsAPI.makeBatchCall,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      qc.invalidateQueries({ queryKey: ["salesCalls"] });
      setShowBatch(false);
      setSelectedLeads([]);
      setBatchCallName("");
      toast.success("Batch call initiated");
    },
    onError: (e) => toast.error(`Batch call failed: ${e?.message || "Unknown error"}`),
  });

  const batchCallFilteredM = useMutation({
    mutationFn: ({ filters, callName }) => salesCallsAPI.makeBatchCallFiltered(filters, callName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesLeads"] });
      qc.invalidateQueries({ queryKey: ["salesCalls"] });
      setShowBatch(false);
      setSelectedLeads([]);
      setBatchCallName("");
      toast.success("Batch call initiated for all filtered leads");
    },
    onError: (e) => toast.error(`Batch call failed: ${e?.message || "Unknown error"}`),
  });

  const openCallDetails = (call) => {
    setSelectedCall(call);
    setShowCallDetails(true);
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setEditForm({
      name: lead?.name || "",
      service: lead?.service || "",
      phone_number: lead?.phone_number || "",
      address: lead?.address || "",
      city: lead?.city || "",
      state: lead?.state || "",
      year: lead?.year || "",
    });
    setShowEdit(true);
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId) 
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (batchLeadsQ.data?.results) {
      const allIds = batchLeadsQ.data.results.map(lead => lead.id);
      setSelectedLeads(prev => 
        prev.length === allIds.length ? [] : allIds
      );
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await salesCallsAPI.exportSalesCallsCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sales_calls.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sales calls exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export sales calls');
    }
  };

  const pager = (data, page, setPage) => {
    if (!data || !data.total || data.total <= data.page_size) return null;
    return (
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
        <div className="text-center text-sm-start">
          Showing {(page - 1) * data.page_size + 1} to {Math.min(page * data.page_size, data.total)} of {data.total}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="align-self-center d-none d-sm-inline">
            Page {data.page} of {data.pages}
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={page >= data.pages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="lead-management">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <h2 className="m-0 mb-3 mb-md-0">Sales Calls Management</h2>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <CSVUploader
            onUpload={(file) => {
              const fd = new FormData();
              fd.append("file", file);
              importLeadsM.mutate(fd);
            }}
          />
          <Button variant="outline-success" onClick={handleExportCSV}>
            <FaDownload className="me-2" />
            Export CSV
          </Button>
          <Button variant="success" onClick={() => setShowBatch(true)}>
            <FaPhone className="me-2" />
            Create Sale Campaign
          </Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <FaPlus className="me-2" />
            Create Lead
          </Button>
        </div>
      </div>

      <Row className="mb-3">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
        <Form.Control
  value={searchInput}
  onChange={(e) => setSearchInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearch(searchInput.trim());
    }
  }}
  placeholder={activeTab === "leads" ? "Search leads..." : "Search calls..."}
/>
<div className="d-flex gap-2 mt-2 mt-md-0">
  <Button
    variant="outline-primary"
    onClick={() => setSearch(searchInput.trim())}
  >
    Search
  </Button>

  <Button
    variant="outline-secondary"
    onClick={() => {
      setSearchInput("");
      setSearch("");
    }}
  >
    Clear
  </Button>
</div>


        </Col>

        <Col xs={12} md={6}>
          {activeTab === "leads" ? (
            <div className="d-flex flex-column flex-sm-row gap-2">
              <Form.Select value={leadFilters.call_status} onChange={(e) => setLeadFilters((p) => ({ ...p, call_status: e.target.value }))}>
                <option value="">All Call Status</option>
                <option value="not_called">Not Called</option>
                <option value="started">Started</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="no_answer">No Answer</option>
                <option value="voicemail">Voicemail</option>
                <option value="failed">Failed</option>
                <option value="invalid_number">Invalid Number</option> 
              </Form.Select>
              <Form.Select
                value={leadFilters.qualified}
                onChange={(e) => setLeadFilters((p) => ({ ...p, qualified: e.target.value }))}
              >
                <option value="">All Qualified</option>
                <option value="true">Qualified</option>
                <option value="false">Not Qualified</option>
                <option value="null">Unknown</option>
              </Form.Select>
            </div>
          ) : (
            <div className="d-flex flex-column flex-sm-row gap-2">
              <Form.Select value={callFilters.status} onChange={(e) => setCallFilters((p) => ({ ...p, status: e.target.value }))}>
                <option value="">All Status</option>
                <option value="started">Started</option>
                <option value="completed">Completed</option>
                <option value="no_answer">No Answer</option>
                <option value="failed">Failed</option>
              </Form.Select>
              <Form.Select
                value={callFilters.interest}
                onChange={(e) => setCallFilters((p) => ({ ...p, interest: e.target.value }))}
              >
                <option value="">All Interest</option>
                <option value="yes">Interested</option>
                <option value="no">Not Interested</option>
                <option value="unclear">Unclear</option>
              </Form.Select>
            </div>
          )}
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "leads")} className="mb-4">
        <Tab eventKey="leads" title="Sales Leads">
          {leadsQ.isLoading ? (
            <LoadingSpinner />
          ) : leadsQ.error ? (
            <Alert variant="danger">Error loading leads: {leadsQ.error.message}</Alert>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {leadsQ.data?.results?.length ? (
                  leadsQ.data.results.map((lead) => (
                    <Card key={lead.id} className="mb-3">
                      <Card.Body>
                        <Card.Title>{lead.name}</Card.Title>
                        <Card.Text><strong>Phone:</strong> {lead.phone_number}</Card.Text>
                        <Card.Text><strong>Service:</strong> {lead.service || "-"}</Card.Text>
                        <Card.Text><strong>Location:</strong> {lead.address && lead.city && lead.state ? `${lead.city}, ${lead.state}` : lead.address || "-"}</Card.Text>
                        <Card.Text><strong>Call Status:</strong> <StatusBadge status={lead.call_status} /></Card.Text>
                        <Card.Text><strong>Qualified:</strong> 
                          {lead.qualified === true ? (
                            <Badge bg="success">Yes</Badge>
                          ) : lead.qualified === false ? (
                            <Badge bg="danger">No</Badge>
                          ) : (
                            <Badge bg="warning">Unknown</Badge>
                          )}
                        </Card.Text>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => singleCallM.mutate({ lead_id: lead.id })}
                            disabled={singleCallM.isPending}
                            title="Call"
                          >
                            {singleCallM.isPending ? <FaSpinner className="fa-spin" /> : <FaPhone />}
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => openEdit(lead)} title="Edit">
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ${lead.name}?`)) {
                                deleteLeadM.mutate(lead.id);
                              }
                            }}
                            disabled={deleteLeadM.isPending}
                            title="Delete"
                          >
                            {deleteLeadM.isPending ? <FaSpinner className="fa-spin" /> : <FaTrash />}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <Alert variant="info" className="text-center">No leads found</Alert>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Service</th>
                        <th>Location</th>
                        <th>Call Status</th>
                        <th>Qualified</th>
                        <th style={{ width: 110 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsQ.data?.results?.length ? (
                        leadsQ.data.results.map((lead) => (
                          <tr key={lead.id}>
                            <td>{lead.name}</td>
                            <td>{lead.phone_number}</td>
                            <td>{lead.service || "-"}</td>
<td>{formatLocation(lead)}</td>
<td><StatusBadge status={lead.call_status} /></td>
                            <td>
                              {lead.qualified === true ? (
                                <Badge bg="success">Yes</Badge>
                              ) : lead.qualified === false ? (
                                <Badge bg="danger">No</Badge>
                              ) : (
                                <Badge bg="warning">Unknown</Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => singleCallM.mutate({ lead_id: lead.id })}
                                  disabled={singleCallM.isPending}
                                  title="Call"
                                >
                                  {singleCallM.isPending ? <FaSpinner className="fa-spin" /> : <FaPhone />}
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={() => openEdit(lead)} title="Edit">
                                  <FaEdit />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete ${lead.name}?`)) {
                                      deleteLeadM.mutate(lead.id);
                                    }
                                  }}
                                  disabled={deleteLeadM.isPending}
                                  title="Delete"
                                >
                                  {deleteLeadM.isPending ? <FaSpinner className="fa-spin" /> : <FaTrash />}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            No leads found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {pager(leadsQ.data, leadsPage, setLeadsPage)}
            </>
          )}
        </Tab>

        <Tab eventKey="calls" title="Sales Calls">
          {callsQ.isLoading ? (
            <LoadingSpinner />
          ) : callsQ.error ? (
            <Alert variant="danger">Error loading calls: {callsQ.error.message}</Alert>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {callsQ.data?.results?.length ? (
                  callsQ.data.results.map((call) => (
                    <Card key={call.id} className="mb-3">
                      <Card.Body>
                        <Card.Title>{call.customer_name || "-"}</Card.Title>
                        <Card.Text><strong>Phone:</strong> {call.phone_number || "-"}</Card.Text>
                        <Card.Text><strong>Status:</strong> <StatusBadge status={call.status} /></Card.Text>
                        <Card.Text><strong>Interest:</strong> {call.interest ? <InterestBadge interest={call.interest} /> : "-"}</Card.Text>
                        <Card.Text><strong>Voicemail:</strong> {call.voicemail ? <Badge bg={call.voicemail === "yes" ? "warning" : "secondary"}>{call.voicemail}</Badge> : "-"}</Card.Text>
                        <Card.Text><strong>Address:</strong> {call.address}</Card.Text>
                        <Button size="sm" variant="outline-info" onClick={() => openCallDetails(call)} title="Details">
                          Details
                        </Button>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <Alert variant="info" className="text-center">No calls found</Alert>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Phone</th>
                        <th>Address</th>
                        <th>Interest</th>
                        <th>Voicemail</th>
                        <th>Status</th>
                        <th style={{ width: 80 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callsQ.data?.results?.length ? (
                        callsQ.data.results.map((call) => (
                          <tr key={call.id}>
                            <td>{call.customer_name || "-"}</td>
                            <td>{call.phone_number || "-"}</td>
                            <td>{call.address }</td>
                            <td>{call.interest ? <InterestBadge interest={call.interest} /> : "-"}</td>
                            <td>{call.voicemail ? <Badge bg={call.voicemail === "yes" ? "warning" : "secondary"}>{call.voicemail}</Badge> : "-"}</td>
                            <td><StatusBadge status={call.status} /></td>
                            <td>
                              <Button size="sm" variant="outline-info" onClick={() => openCallDetails(call)} title="Details">
                                Details
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            No calls found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {pager(callsQ.data, callsPage, setCallsPage)}
            </>
          )}
        </Tab>
      </Tabs>

      {/* Create Lead Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Sales Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control value={createForm.name} onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    value={createForm.phone_number}
                    onChange={(e) => setCreateForm((p) => ({ ...p, phone_number: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Service</Form.Label>
              <Form.Control value={createForm.service} onChange={(e) => setCreateForm((p) => ({ ...p, service: e.target.value }))} />
            </Form.Group>

            <Row>
              <Col xs={12} md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control value={createForm.address} onChange={(e) => setCreateForm((p) => ({ ...p, address: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="number"
                    value={createForm.year}
                    onChange={(e) => setCreateForm((p) => ({ ...p, year: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control value={createForm.city} onChange={(e) => setCreateForm((p) => ({ ...p, city: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control value={createForm.state} onChange={(e) => setCreateForm((p) => ({ ...p, state: e.target.value }))} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="flex-column flex-sm-row">
          <Button variant="secondary" onClick={() => setShowCreate(false)} className="mb-2 mb-sm-0">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => createLeadM.mutate(createForm)}
            disabled={createLeadM.isPending || !createForm.name || !createForm.phone_number}
          >
            {createLeadM.isPending ? <FaSpinner className="fa-spin me-2" /> : null}
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Sales Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    value={editForm.phone_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone_number: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Service</Form.Label>
              <Form.Control value={editForm.service} onChange={(e) => setEditForm((p) => ({ ...p, service: e.target.value }))} />
            </Form.Group>

            <Row>
              <Col xs={12} md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Year</Form.Label>
                  <Form.Control type="number" value={editForm.year} onChange={(e) => setEditForm((p) => ({ ...p, year: e.target.value }))} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control value={editForm.city} onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control value={editForm.state} onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="flex-column flex-sm-row">
          <Button variant="secondary" onClick={() => setShowEdit(false)} className="mb-2 mb-sm-0">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => updateLeadM.mutate({ id: editingLead?.id, data: editForm })}
            disabled={updateLeadM.isPending || !editingLead?.id}
          >
            {updateLeadM.isPending ? <FaSpinner className="fa-spin me-2" /> : null}
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Batch Call Modal */}
      <BatchCallModal
        show={showBatch}
        onHide={() => setShowBatch(false)}
        batchLeadsQ={batchLeadsQ}
        batchFilters={batchFilters}
        setBatchFilters={setBatchFilters}
        selectedLeads={selectedLeads}
        setSelectedLeads={setSelectedLeads}
        batchCallName={batchCallName}
        setBatchCallName={setBatchCallName}
        batchCallM={batchCallM}
        batchCallFilteredM={batchCallFilteredM}
        handleSelectLead={handleSelectLead}
        handleSelectAll={handleSelectAll}
        batchLeadsPage={batchLeadsPage}
        setBatchLeadsPage={setBatchLeadsPage}
        pager={pager}
      />

      {/* Call Details Modal */}
      <CallDetailsModal
        show={showCallDetails}
        onHide={() => setShowCallDetails(false)}
        call={selectedCall}
      />
    </Container>
  );
}
